import axiosInstance from 'config/axiosInstance';

/**
 * EV-493。選ぶ欄の候補を、全件ではなくサーバーの検索とページ送りで読む。
 *
 * 以前は候補のために一覧を全件（100件ずつ最大50回、5,000件で止める共通の処理。EV-493 で外した）読んでいた。
 * 候補は数十件ずつ読み、続きは「もっと見る」（またはスクロール）で読む。絞り込みはサーバーで行う。
 */

/** 1回に読む候補の数。 */
export const PICKER_PAGE_SIZE = 20;

/** 入力が止まってから検索の要求を出すまでの待ち（ミリ秒）。 */
export const PICKER_DEBOUNCE_MS = 300;

/** 候補の1ページ。total はサーバーの件数（分からなければ undefined）。 */
export interface RemotePage<T> {
  items: T[];
  hasMore: boolean;
  total?: number;
}

/** 検索の文字（前後の空白を除いた値。空なら絞らない）と、1から始まるページ番号で1ページを読む。 */
export type FetchRemotePage<T> = (query: string, page: number) => Promise<RemotePage<T>>;

type Params = Record<string, string | number | boolean | null | undefined>;

/** DRF の一覧（{count, next, results}）を、検索（search）・ページ・絞り込みの条件で1ページ読む。 */
export async function fetchSearchPage<T>(
  path: string,
  query: string,
  page: number,
  params: Params = {},
  pageSize: number = PICKER_PAGE_SIZE
): Promise<RemotePage<T>> {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const text = query.trim();
  if (text) search.set('search', text);
  search.set('page', String(page));
  search.set('page_size', String(pageSize));
  try {
    const response = await axiosInstance.get(`${path}?${search.toString()}`);
    const data = response.data ?? {};
    const items: T[] = Array.isArray(data.results) ? data.results : [];
    return {
      items,
      hasMore: Boolean(data.next),
      total: typeof data.count === 'number' ? data.count : undefined,
    };
  } catch (error) {
    // 続きのページを読む間に件数が減り、そのページが無くなった（DRF は 404）。続きは無いとして扱う。
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (page > 1 && status === 404) return { items: [], hasMore: false };
    throw error;
  }
}
